import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserSelfDetails } from '@steelbuy/data-access';
import { UserRole } from '@steelbuy/ts-shared';
import { ListingDetailsExpiration } from '@steelbuy/ui-domain';
import {
    ButtonCallToAction,
    ButtonPrimary,
    ButtonTertiaryOnLightM,
    Card,
    CardContentAttributes,
    CardContentAttributesItem,
    CardLayout,
    FormActionbar,
    LoadingStatus,
} from '@steelbuy/ui-primitive';
import { RoutePath } from '../../../router/Routes';
import { RFQTeaser } from '../../create-rfq/rfq-teaser/RFQTeaser';
import LegalCheckboxes from '../../legal-checkboxes/LegalCheckboxes';
import { useBulkListingContext } from '../bulk-listing-context/BulkListingContext';
import { BulkListingTable } from '../bulk-listing-table/BulkListingTable';
import { BulkListing, BulkUploadForm, getFilteredHeadings, getListingsGrouped, getStepRoute } from '../bulkListingUtil';

import './BulkListingSummary.scss';

type FileSet = Set<string>;

type FileSets = { testCerts: FileSet; pictures: FileSet };

export const BulkListingSummary = () => {
    const { draftId = '' } = useParams();
    const unblockOnSubmit = useRef<boolean>(false);
    const [validity, setValidity] = useState(false);
    const { t } = useTranslation(['translation', 'uiDomain']);
    const navigate = useNavigate();
    const { state } = useLocation();
    const { handleSaveDraft, updateBulkUploadMutation, draftSavedSuccessCallback } = useBulkListingContext();
    const { getValues, reset } = useFormContext();
    const formData = getValues();
    const { fields } = useFieldArray({
        name: 'listings',
    });
    const { data: userData } = useUserSelfDetails();
    const hasSellerRole = userData?.roles.includes(UserRole.SELLER) ?? false;

    useEffect(() => {
        if (fields.length === 0) {
            navigate(getStepRoute(1), { replace: true });
        }
        draftSavedSuccessCallback(() => {
            if (unblockOnSubmit.current) {
                reset();
                navigate(RoutePath.CREATE_BULK_LISTING_SUCCESS, { replace: true });
            }
        });
    }, []);

    if (fields.length === 0) {
        return null;
    }

    const files = getValues('listings')?.reduce(
        (acc: FileSets, listing: BulkListing) => {
            if (listing.testCertificate?.documentId) {
                acc.testCerts.add(listing.testCertificate?.documentId);
            }
            if (listing.picture?.documentId) {
                acc.pictures.add(listing.picture?.documentId);
            }
            return acc;
        },
        { testCerts: new Set(), pictures: new Set() } as FileSets
    );

    const testCertCount = files.testCerts.size;
    const pictureCount = files.pictures.size;

    const groupedListings = getListingsGrouped(formData as BulkUploadForm, fields as BulkListing[]);

    return (
        <div className="bulk-listing-summary">
            <Card isClickable={false}>
                <CardLayout>
                    <CardContentAttributes header={t('application.createBulkListing.summary.listings')}>
                        <CardContentAttributesItem
                            label={t('application.createBulkListing.summary.totalListings')}
                            value={`${fields.length}`}
                        />
                        <CardContentAttributesItem
                            label={t('application.createBulkListing.summary.totalDocuments')}
                            value={`${testCertCount}`}
                        />
                        {!formData.prime && (
                            <CardContentAttributesItem
                                label={t('application.createBulkListing.summary.totalImages')}
                                value={`${pictureCount}`}
                            />
                        )}
                    </CardContentAttributes>
                </CardLayout>
            </Card>
            {groupedListings.map((listingGroup) => (
                <div className="bulk-listing-summary-import-group-item" key={listingGroup.groupValue}>
                    <RFQTeaser
                        material={{ ...formData, [listingGroup.groupedBy]: listingGroup.groupValue }}
                        showQuality
                    />
                    <BulkListingTable
                        listingsData={listingGroup.listings}
                        tableHeadings={getFilteredHeadings(
                            {
                                ...formData,
                                [listingGroup.groupedBy]: listingGroup.groupValue,
                            } as BulkUploadForm,
                            true
                        )}
                        formData={formData as BulkUploadForm}
                    />
                </div>
            ))}
            <ListingDetailsExpiration expiryDate={formData.expireDate} autoRenew={formData.autoRenew} showAutoRenew />

            {hasSellerRole && <LegalCheckboxes setValidity={setValidity} showSellerTncs />}
            <FormActionbar>
                <ButtonTertiaryOnLightM
                    label={t('uiDomain:common.edit')}
                    onClick={() => navigate(getStepRoute(3, draftId), { state })}
                />

                {hasSellerRole ? (
                    <ButtonCallToAction
                        disabled={!validity}
                        loadingStatus={updateBulkUploadMutation.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                        label={t('translation:application.createListingSummary.submitButtonLabel')}
                        onClick={() => {
                            unblockOnSubmit.current = true;
                            handleSaveDraft(draftId, true);
                        }}
                    />
                ) : (
                    <ButtonPrimary
                        label={t('application.createRFQ.saveAsDraft')}
                        onClick={() => handleSaveDraft(draftId)}
                        loadingStatus={updateBulkUploadMutation.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                    />
                )}
            </FormActionbar>
        </div>
    );
};
